import { fromZonedTime } from "date-fns-tz";
import { addDaysKey, CENTRAL_TZ } from "@shared/weeks";
import type { TaskLinkRef } from "@shared/taskLinks";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n: number) => String(n).padStart(2, "0");

/** The viewer's calendar day, YYYY-MM-DD. */
export function localYmd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "Today", "Yesterday", "Sep 30", or "Dec 9, 2025". Takes a YYYY-MM-DD day or an ISO instant. */
export function dayLabel(value: string | null | undefined, today: string): string {
  if (!value) return "";
  const ymd = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : localYmd(new Date(value));
  if (ymd === today) return "Today";
  if (ymd === addDaysKey(today, -1)) return "Yesterday";
  const [y, m, d] = ymd.split("-").map(Number);
  return y === Number(today.slice(0, 4)) ? `${MONTHS[m - 1]} ${d}` : `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** First word of a name; an email address stays whole. */
export function firstName(name: string | null | undefined): string {
  if (!name) return "";
  return name.includes("@") ? name : name.trim().split(/\s+/)[0];
}

export function visiblePeople<T>(people: T[], showAll: boolean, limit = 6): T[] {
  return showAll ? people : people.slice(0, limit);
}

/** L opens the log form, unless the person is typing, holding a modifier, or in a dialog. */
export function isLogShortcut(
  e: { key: string; metaKey: boolean; ctrlKey: boolean; altKey: boolean },
  activeTag: string | undefined,
  editable: boolean,
  dialogOpen: boolean,
): boolean {
  if (e.key !== "l" && e.key !== "L") return false;
  if (e.metaKey || e.ctrlKey || e.altKey || editable || dialogOpen) return false;
  return !["INPUT", "TEXTAREA", "SELECT"].includes(activeTag ?? "");
}

/** "acme.com" from "https://www.acme.com/about"; null when blank. */
export function websiteHost(url: string | null | undefined): string | null {
  const t = url?.trim();
  if (!t) return null;
  return t.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split(/[/?#]/)[0] || null;
}

/** A link that works whether or not the stored value has a scheme. */
export function websiteHref(url: string | null | undefined): string | null {
  const t = url?.trim();
  if (!t) return null;
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}

/** The list a company belongs to, where archiving it returns you. COI-category firms have no list. */
export function listPathFor(category: string | null | undefined): string {
  switch (category) {
    case "Buyer Network": return "/buyers";
    case "Client": return "/clients";
    case "Prospects": return "/prospects";
    case "Capital Providers": return "/capital-providers";
    default: return "/contacts";
  }
}

/** The person a company page leads with: the one marked primary, else the first listed. */
export function primaryContact<T extends { isPrimary?: boolean | null }>(people: T[]): T | null {
  return people.find((p) => p.isPrimary) ?? people[0] ?? null;
}

const SOURCE: Record<string, string> = {
  manual: "Manual entry", business_card: "Business card scan", bulk_upload: "Bulk upload", outlook: "Outlook",
  chrome_extension: "Chrome extension", website: "Website", onepager: "One-pager", mcp: "MCP",
};

/** How a record entered the CRM. */
export function sourceLabel(source: string | null | undefined): string {
  if (!source) return "Manual entry";
  return SOURCE[source] ?? source.replace(/_/g, " ").replace(/^\w/, (ch) => ch.toUpperCase());
}

/** "Mar 5, 2026", or "Unknown" for nothing. */
export const fullDay = (iso: string | null | undefined) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Unknown";

/** "Oct 23, 2026" from a stored day (YYYY-MM-DD). Never through new Date, which reads it as UTC midnight and shows the day before west of UTC. */
export function fullDayOfYmd(ymd: string | null | undefined): string {
  const m = ymd?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : "";
}

/** A record page's target: the company, or a person (with their firm, on person and COI pages). */
export interface RecordTarget { contactId?: string; companyId?: string }

/**
 * The record a page is about. A person or COI page passes the person AND their
 * firm, and the page is about the person, so the person wins; taking the firm
 * would chip "Jane Smith" on Jane's own page and hide her firm.
 */
export function currentRecordRef(t: RecordTarget): TaskLinkRef | null {
  if (t.contactId) return { kind: "contact", id: t.contactId };
  if (t.companyId) return { kind: "company", id: t.companyId };
  return null;
}

/** Every record a Next step links through its target, so the picker's cap counts them all. */
export function targetLinkRefs(t: RecordTarget): TaskLinkRef[] {
  return [
    ...(t.companyId ? [{ kind: "company" as const, id: t.companyId }] : []),
    ...(t.contactId ? [{ kind: "contact" as const, id: t.contactId }] : []),
  ];
}

/**
 * POST /api/tasks body for a next step. The record it is written on goes in
 * contactId / companyId as before; `links` carries any other records picked
 * with "+ Link another record", and is left off when there are none.
 */
export function nextStepBody(i: {
  title: string; ownerId: string; dueYmd: string | null; contactId?: string; companyId?: string; links?: TaskLinkRef[];
}) {
  return {
    title: i.title,
    assignedToId: i.ownerId,
    dueAt: i.dueYmd ? fromZonedTime(`${i.dueYmd}T12:00:00`, CENTRAL_TZ).toISOString() : null,
    ...(i.contactId ? { contactId: i.contactId } : {}),
    ...(i.companyId ? { companyId: i.companyId } : {}),
    ...(i.links?.length ? { links: i.links } : {}),
  };
}

/** Save `text` as a plain-text file through the browser (Blob, object URL, anchor click, revoke). */
export function downloadText(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** "Mar 2027" from a stored month ("2027-03-01" or "2027-03"); empty for nothing. */
export function monthLabel(value: string | null | undefined): string {
  const m = value?.match(/^(\d{4})-(\d{2})/);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : "";
}

/** dayLabel for use inside a phrase: "today" and "yesterday" lose their capital ("was due yesterday"). */
export function inlineDayLabel(value: string | null | undefined, today: string): string {
  const label = dayLabel(value, today);
  return label === "Today" || label === "Yesterday" ? label.toLowerCase() : label;
}
